/**
 * WAF geo reference catalog — bundled static data (country / region / city /
 * network hierarchy) shared across WAF features (analytics country labels, geo
 * filters, …). `code` is the vendor/ISO code; `name` is a camelCase key
 * (`humanizeGeoName` for display). All lookups are indexed once at module load,
 * so getters are O(1). Data lives next to this file as `geo-*.json`.
 *
 * ~70 KB total and only pulled into the (route-split) WAF chunk; if it ever needs
 * to stay out of the initial WAF bundle, switch the imports to dynamic `import()`.
 */
import countriesRaw from './geo-countries.json';
import regionsRaw from './geo-regions.json';
import citiesRaw from './geo-cities.json';
import networksRaw from './geo-networks.json';

export interface GeoCountry {
  /** ISO 3166-1 alpha-2 code, e.g. "VN". */
  code: string;
  /** camelCase name key, e.g. "unitedArabEmirates". */
  name: string;
  /** Continent code, e.g. "AS", "EU". */
  continent: string;
  /** Whether the country has sub-regions in `geoRegions`. */
  hasRegions: boolean;
}

export interface GeoRegion {
  /** Parent country code. */
  country: string;
  /** Region code (unique within the country). */
  code: string;
  /** camelCase name key. */
  name: string;
}

export interface GeoCity {
  /** Parent country code. */
  country: string;
  /** City code (unique within the country). */
  code: string;
  /** camelCase name key. */
  name: string;
  /** Parent region code. */
  region: string;
  /** Whether the city has networks in `geoNetworks`. */
  hasNetwork: boolean;
}

export interface GeoNetwork {
  /** Parent country code. */
  country: string;
  /** Network code, e.g. "CT" (China Telecom). */
  code: string;
  /** camelCase name key. */
  name: string;
}

export const geoCountries = countriesRaw as unknown as GeoCountry[];
export const geoRegions = regionsRaw as unknown as GeoRegion[];
export const geoCities = citiesRaw as unknown as GeoCity[];
export const geoNetworks = networksRaw as unknown as GeoNetwork[];

// ---- Indexes (built once) ------------------------------------------------

function groupBy<T>(arr: T[], key: (t: T) => string): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const it of arr) {
    const k = key(it);
    const list = m.get(k);
    if (list) list.push(it);
    else m.set(k, [it]);
  }
  return m;
}

const countryByCode = new Map(geoCountries.map((c) => [c.code, c]));
const regionsByCountry = groupBy(geoRegions, (r) => r.country);
const regionByKey = new Map(geoRegions.map((r) => [`${r.country}:${r.code}`, r]));
const citiesByCountry = groupBy(geoCities, (c) => c.country);
const cityByKey = new Map(geoCities.map((c) => [`${c.country}:${c.code}`, c]));
const networksByCountry = groupBy(geoNetworks, (n) => n.country);
const networkByKey = new Map(geoNetworks.map((n) => [`${n.country}:${n.code}`, n]));

// ---- Display ------------------------------------------------------------

/** camelCase name key → human "Title Case" label, e.g. "unitedArabEmirates" → "United Arab Emirates". */
export function humanizeGeoName(name: string): string {
  if (!name) return '';
  const spaced = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase → words
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // ACRONYMWord → ACRONYM Word
    .replace(/\s+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ---- Getters ------------------------------------------------------------

/** Country record by ISO code (undefined if unknown). */
export const getGeoCountry = (code: string): GeoCountry | undefined => countryByCode.get(code);
/** Display country name for an ISO code; falls back to the raw code. */
export const countryName = (code: string): string => {
  const c = countryByCode.get(code);
  return c ? humanizeGeoName(c.name) : code;
};

/** Regions of a country (empty if none). */
export const regionsOf = (country: string): GeoRegion[] => regionsByCountry.get(country) ?? [];
export const getGeoRegion = (country: string, code: string): GeoRegion | undefined =>
  regionByKey.get(`${country}:${code}`);
export const regionName = (country: string, code: string): string => {
  const r = regionByKey.get(`${country}:${code}`);
  return r ? humanizeGeoName(r.name) : code;
};

/** Cities of a country, optionally filtered by region. */
export const citiesOf = (country: string, region?: string): GeoCity[] => {
  const list = citiesByCountry.get(country) ?? [];
  return region ? list.filter((c) => c.region === region) : list;
};
export const getGeoCity = (country: string, code: string): GeoCity | undefined =>
  cityByKey.get(`${country}:${code}`);
export const cityName = (country: string, code: string): string => {
  const c = cityByKey.get(`${country}:${code}`);
  return c ? humanizeGeoName(c.name) : code;
};

// ---- Access-control geo codes -------------------------------------------

/**
 * Country-level access-control geo code, full 5-segment form
 * `"{continent}-{country}-00-00-00"`, e.g. `"EU-AT-00-00-00"`. The trailing
 * `-00-00-00` are region/city/network wildcards (the whole country). The
 * Mlytics provider stores the value verbatim — it does NOT pad a shorter
 * `"EU-AT"` — so we always emit the full form (matches the SDK/routing tests).
 */
export const acGeoCode = (c: GeoCountry): string => `${c.continent}-${c.code}-00-00-00`;

/** Country ISO code out of an access-control geo value (2nd `-`-segment). */
export const acGeoCountry = (value: string): string => value.split('-')[1] ?? value;

/** Networks of a country (empty if none). */
export const networksOf = (country: string): GeoNetwork[] => networksByCountry.get(country) ?? [];
export const getGeoNetwork = (country: string, code: string): GeoNetwork | undefined =>
  networkByKey.get(`${country}:${code}`);
export const networkName = (country: string, code: string): string => {
  const n = networkByKey.get(`${country}:${code}`);
  return n ? humanizeGeoName(n.name) : code;
};
